import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { authService, checkUserIsActive } from '@/services/auth';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Handle user deactivation check separately to avoid Supabase deadlock
  const validateUserStatus = useCallback(async (currentUser: User) => {
    const isActive = await checkUserIsActive(currentUser.id);
    if (!isActive) {
      await authService.signOut();
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = authService.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);

        // Defer user validation using queueMicrotask for cleaner async handling
        if (newSession?.user && event === 'SIGNED_IN') {
          queueMicrotask(() => validateUserStatus(newSession.user));
        }
      }
    );

    // THEN check for existing session
    authService.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [validateUserStatus]);

  // Simplified handlers that delegate to service
  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const result = await authService.signUp(email, password, fullName);
    return { error: result.error as Error | null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authService.signIn(email, password);
    return { error: result.error as Error | null };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const result = await authService.signInWithOAuth('google');
    return { error: result.error as Error | null };
  }, []);

  const signInWithApple = useCallback(async () => {
    const result = await authService.signInWithOAuth('apple');
    return { error: result.error as Error | null };
  }, []);

  const signOut = useCallback(async () => {
    await authService.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const result = await authService.resetPassword(email);
    return { error: result.error as Error | null };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
